import React from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
  Checkbox, Box, Stack, Card, CardHeader, CardContent, Divider,
  Chip, Tooltip, LinearProgress, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, TextField, InputAdornment
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SearchIcon from '@mui/icons-material/Search';
import * as ETVConstants from '../ETVConstants';

class SelectAvailableScreensNew extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      items: [],
      groups: [],
      selected: [],
      isLoaded: false,
      error: null,
      screenQuery: '',
    };
  }
  
  componentDidMount() {
    this.loadItems();
  }

  componentDidUpdate(prevProps) {
    // Reload if user changes or dialog reopens and we never loaded
    if (prevProps.user_id !== this.props.user_id && this.props.user_id) {
      this.loadItems();
    }
    if (this.props.isOpen && !prevProps.isOpen && !this.state.isLoaded) {
      this.loadItems();
    }
  }

  // ---------- Data ----------
  loadItems() {
    this.setState({ loading: true });
    if (!this.props.user_id) {
      this.setState({ loading: false, isLoaded: true });
      return;
    }

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/device_mgmt?action=list&owner_id=${this.props.user_id}`;

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          const groups = ETVConstants.filterOutEmptyGroups(result.groups);
          const sorted = this.filterExcludedScreens(result.list).sort(ETVConstants.compareDevice);
          this.setState({
            items: sorted,
            groups,
            loading: false,
            isLoaded: true,
            selected: Array.isArray(this.props.selected) ? [...this.props.selected] : [],
            error: null,
          });
        },
        (error) => {
          this.setState({
            groups: [],
            items: [],
            loading: false,
            isLoaded: true,
            selected: Array.isArray(this.props.selected) ? [...this.props.selected] : [],
            error,
          });
        }
      );
  }

  filterExcludedScreens(list) {
    if (!this.props.excluded_screen_ids) return list || [];
    const excl = this.props.excluded_screen_ids;
    return (list || []).filter((it) => !excl.includes(it.id));
  }

  // ---------- Selection helpers ----------
  isSelectedScreen = (deviceID) => Array.isArray(this.state.selected) && this.state.selected.includes(deviceID);

  getScreensInGroup = (groupID) => {
    const g = (this.state.groups || []).find((x) => x.group_id === groupID);
    return g?.device_ids || [];
  };

  groupSelectionState = (groupID) => {
    const screens = this.getScreensInGroup(groupID);
    if (!screens.length) return 'none';
    const selectedInGroup = screens.filter((id) => this.isSelectedScreen(id)).length;
    if (selectedInGroup === 0) return 'none';
    if (selectedInGroup === screens.length) return 'all';
    return 'some';
  };

  addScreen = (screenID) =>
    this.setState((s) => (s.selected.includes(screenID) ? null : { selected: [...s.selected, screenID] }));

  removeScreen = (screenID) =>
    this.setState((s) => ({ selected: s.selected.filter((id) => id !== screenID) }));

  toggleScreen = (screenID) =>
    this.isSelectedScreen(screenID) ? this.removeScreen(screenID) : this.addScreen(screenID);

  addGroup = (groupID) => {
    const screens = this.getScreensInGroup(groupID);
    this.setState((s) => {
      const set = new Set(s.selected);
      screens.forEach((id) => set.add(id));
      return { selected: Array.from(set) };
    });
  };

  removeGroup = (groupID) => {
    const screens = this.getScreensInGroup(groupID);
    this.setState((s) => ({ selected: s.selected.filter((id) => !screens.includes(id)) }));
  };

  toggleGroup = (groupID) => {
    const state = this.groupSelectionState(groupID);
    if (state === 'all' || state === 'some') this.removeGroup(groupID);
    else this.addGroup(groupID);
  };

  clearSelection = () => this.setState({ selected: [] });

  // ---------- Render ----------
  render() {
    const { items, groups, selected, isLoaded, error, loading, screenQuery } = this.state;
    const selectedCount = selected.length;

    // Quick search (name/location/address/city/zip)
    const q = screenQuery.trim().toLowerCase();
    const filteredItems = !q
      ? items
      : items.filter((row) => {
          const hay = [row.device_name, row.location_name, row.address, row.city, row.zip]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return hay.includes(q);
        });

    const HEADER_H = 40; // equal header height (px)

    return (
      <Dialog
        open={this.props.isOpen}
        maxWidth="lg"
        fullWidth
        onClose={() => this.props.closeHook?.()}
        aria-labelledby="select-screens-title">
        <DialogTitle id="select-screens-title">{ETVConstants.trans('select_screens')}</DialogTitle>

        <DialogContent dividers sx={{ pt: 2, overflowX: 'hidden' }}>
          {loading && <LinearProgress />}

          {!isLoaded ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <LinearProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{ETVConstants.trans('failed_to_load')}</Typography>
          ) : (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minWidth: 0 }}>
              {/* Groups */}
              <Card sx={{ flex: { xs: '1 1 auto', md: '0 0 360px' }, overflow: 'hidden' }}>
                <CardHeader
                  sx={(t) => ({
                    px: 2,
                    minHeight: HEADER_H,
                    backgroundColor: t.palette.action.hover,
                    borderBottom: `1px solid ${t.palette.divider}`,
                    '& .MuiCardHeader-content': {
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: HEADER_H,
                    },
                  })}
                  title={
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ width: '100%', minWidth: 0, flexWrap: 'nowrap' }}
                    >
                      <Typography variant="h6">{ETVConstants.trans('groups')}</Typography>
                      {!!groups?.length && (
                        <Chip size="small" variant="outlined" label={`${groups.length}`} sx={{ height: 22, fontWeight: 600 }} />
                      )}
                      <Box sx={{ flex: 1 }} />
                    </Stack>
                  }
                />
                <Divider />
                <CardContent sx={{ pt: 1, px: 0, overflow: 'visible' }}>
                  {groups && groups.length ? (
                    <List dense disablePadding>
                      {groups.map((g) => {
                        const state = this.groupSelectionState(g.group_id);
                        const checked = state === 'all';
                        const indeterminate = state === 'some';
                        return (
                          <ListItem
                            key={g.group_id}
                            secondaryAction={
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${(g.device_ids || []).length}`}
                                sx={{ height: 22 }}
                              />
                            }
                            disablePadding
                          >
                            <ListItemButton onClick={() => this.toggleGroup(g.group_id)}>
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <Checkbox
                                  edge="start"
                                  size="small"
                                  checked={checked}
                                  indeterminate={indeterminate}
                                  tabIndex={-1}
                                  disableRipple
                                  inputProps={{ 'aria-label': ETVConstants.trans('select_group') }}
                                />
                              </ListItemIcon>
                              <ListItemText primary={g.group_name} />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                      {ETVConstants.trans('no_groups')}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Screens */}
              <Card sx={{ flex: '1 1 0', overflow: 'hidden', minWidth: 0 }}>
                <CardHeader
                  sx={(t) => ({
                    px: 2,
                    minHeight: HEADER_H,
                    backgroundColor: t.palette.action.hover,     // ← gray, same as groups
                    borderBottom: `1px solid ${t.palette.divider}`,
                    '& .MuiCardHeader-content': {
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: HEADER_H,
                    },
                  })}
                  title={
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ width: '100%', minWidth: 0, flexWrap: 'nowrap' }}
                    >
                      <Typography variant="h6" sx={{ mr: 1 }}>
                        {ETVConstants.trans('screens')}
                      </Typography>

                      <Chip
                        size="medium"
                        variant="outlined"
                        label={selectedCount}
                        sx={{ height: 22, fontWeight: 600 }}
                      />

                      {/* Select all (visible) */}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const ids = (filteredItems || []).map((x) => x.id);
                          this.setState((s) => {
                            const set = new Set(s.selected);
                            ids.forEach((id) => set.add(id));
                            return { selected: Array.from(set) };
                          });
                        }}
                        disabled={
                          !filteredItems?.length ||
                          filteredItems.every((row) => this.isSelectedScreen(row.id))
                        }
                        sx={{ ml: 1 }}
                      >
                        {ETVConstants.trans('select_all')}
                      </Button>

                      {!!selectedCount && (
                        <Button
                          onClick={this.clearSelection}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1 }}
                          startIcon={<RestartAltIcon />}
                        >
                          {ETVConstants.trans('remove_all')}
                        </Button>
                      )}

                      <Box sx={{ flex: 1, minWidth: 0 }} />

                      <TextField
                        size="small"
                        placeholder={'search...'}
                        value={screenQuery}
                        onChange={(e) => this.setState({ screenQuery: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          width: { xs: '100%', sm: 260 },
                          ml: { xs: 0, sm: 'auto' },
                          maxWidth: '100%',
                          '& .MuiInputBase-root': { height: 32 },       // keep header height stable
                          '& .MuiInputBase-input': { py: 0.5 },
                        }}
                      />
                    </Stack>
                  }
                />
                <Divider />
                <CardContent sx={{ pt: 1, px: 0, overflow: 'visible', minWidth: 0 }}>
                  {filteredItems && filteredItems.length ? (
                    <List dense disablePadding>
                      {filteredItems.map((row) => (
                        <ListItem key={row.id} disablePadding>
                          <ListItemButton onClick={() => this.toggleScreen(row.id)}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Checkbox
                                edge="start"
                                size="small"
                                checked={this.isSelectedScreen(row.id)}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ 'aria-label': String(ETVConstants.trans('select_screen')) }}
                              />
                            </ListItemIcon>

                            {/* Status dot/symbol */}
                            <Box sx={{ width: 20, textAlign: 'center', mr: 1 }}>
                              <Tooltip title={`${String(ETVConstants.trans('last_connected'))}: ${row.last_connected || '-'}`}>
                                <span>{ETVConstants.getLastConnectSymbol(row.last_connected)}</span>
                              </Tooltip>
                            </Box>

                            <ListItemText
                              primary={
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {row.device_name}
                                  </Typography>
                                  {row.location_name && (
                                    <Chip size="small" variant="outlined" label={row.location_name} sx={{ height: 20 }} />
                                  )}
                                </Stack>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {[row.address, row.zip, row.city].filter(Boolean).join(' • ')}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                      {q ? ETVConstants.trans('no_results') || 'No results' : ETVConstants.trans('no_screens_found')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => this.props.closeHook?.()}>{ETVConstants.trans('close')}</Button>
          <Button variant="contained" startIcon={<CheckBoxIcon />} onClick={() => this.props.selectedScreens?.(this.state.selected)}disabled={!selectedCount}>
            {ETVConstants.trans('select')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default SelectAvailableScreensNew;
